import {
  type RestrictivePractices,
  RestrictivePracticeTypeEnum
} from '@lmc/validations'
import { trimmed, withoutBlanks } from '../../stepValues'

/**
 * Where the mobile app records each practice: one yes/no section per type, as
 * the backend's v1 flags map them.
 */
const APP_SECTIONS = {
  chemicalRestraints: RestrictivePracticeTypeEnum.chemicalRestraint,
  environmentalRestraints: RestrictivePracticeTypeEnum.environmentalRestraint,
  physicalIntervention: RestrictivePracticeTypeEnum.physicalIntervention,
  restraints: RestrictivePracticeTypeEnum.mechanicalRestraint,
  verbalRestraints: RestrictivePracticeTypeEnum.verbalRestraint
} as const

type AppSection = keyof typeof APP_SECTIONS

export type AppRestraintAnswers = Partial<
  Record<AppSection, { details?: string; hasBeenApplied?: boolean } | null>
> & { noRestraintsDetails?: string | null }

/**
 * Step 4's answer from what the mobile app recorded, for a case where the step
 * has not been saved yet: Yes with a practice per section applied. No only
 * when the app gave details for it, because the backend writes every section
 * as not applied whether or not the app asked; otherwise unanswered.
 */
export const restrictivePracticesFromApp = (
  answers: AppRestraintAnswers | null | undefined
): RestrictivePractices | undefined => {
  const entries = (Object.keys(APP_SECTIONS) as Array<AppSection>)
    .filter((section) => answers?.[section]?.hasBeenApplied)
    .map((section) => ({
      type: APP_SECTIONS[section],
      ...withoutBlanks({ notes: trimmed(answers?.[section]?.details) })
    }))
  if (entries.length > 0) {
    return { hasBeenApplied: true, entries }
  }
  const details = trimmed(answers?.noRestraintsDetails)
  return details ? { hasBeenApplied: false, details } : undefined
}
